import { Component, type ErrorInfo, type ReactNode } from 'react';

/**
 * The last resort. Every screen handles its own failed request, but a bug in rendering — a shape
 * the API changed, a date that will not parse — throws where no query can catch it, and React
 * answers by unmounting the whole tree. On a phone at a conference that is a white screen with no
 * way out, so this keeps the app's voice and offers the one action that works.
 *
 * Q-09 asks for outages to raise alerts; there is no reporting sink yet, so the error is logged
 * and this is the one place to send it from once there is.
 */
export class AppErrorBoundary extends Component<{ children: ReactNode }, { failed: boolean }> {
  override state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  override componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('Attendee app crashed', error, info.componentStack);
  }

  override render() {
    if (!this.state.failed) {
      return this.props.children;
    }

    return (
      <main className="px-gutter flex min-h-dvh flex-col items-center justify-center text-center">
        <h1 className="font-display text-screen-title font-semibold text-ink">
          Something went wrong
        </h1>
        <p className="text-body mt-2 text-ink-3">
          The app stopped on this screen. Reloading usually clears it. If it keeps happening, tell
          the organizers what you were looking at.
        </p>
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="text-button rounded-button mt-6 flex min-h-touch items-center justify-center bg-ink px-6 font-semibold text-white"
        >
          Reload the app
        </button>
      </main>
    );
  }
}
